import React, { useState, useEffect, useMemo } from "react"
import { useParams } from "react-router-dom"
import { useShallow } from "zustand/react/shallow"
import dayjs from "dayjs"

// components
import { Flex } from "antd"
import PrimeField from "components/PrimeField/PrimeField"
import { PrimeButton } from "components-design-system"
import UploadModal from "./subcomponents/UploadModal/UploadModal"
import GridTypeA from "components/grids/GridTypeA/GridTypeA"
import Modal from "components/Modal/Modal"
import PRSUploadProgress from "components/PRSUploadProgress/PRSUploadProgress"
import { Panel } from "components-design-system"

// api
import { useGetStudy } from "api/hooks/studies/useStudiesApi"
import { useGetAuthoringStudy } from "api/hooks/studies/useAuthoringApi"
import {
  useGetSubmissionsHistory,
  usePostSubmission,
  useDownloadXml,
  useGetPrsSubmissionsUpload,
  usePostPrsSubmissionsUpload,
  useGetApprovalHistory,
} from "api/hooks/studies/useSubmissionsApi"
import { useUserSettings } from "api/hooks"

// cell renderers
import { FormsCellRenderer } from "components/ag-grid-cell-renderers/SubmissionCellRenderers/SubmissionCellRenderers"
import TimeCellRenderer from "components/ag-grid-cell-renderers/TimeCellRenderer"

// context
import useSubmissions from "context/hooks/useSubmissions"
import useAppState from "context/hooks/useAppState"
import useAuthoringViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringViewStore"
import useAuthoringPrintActions from "../Authoring/hooks/actions/useAuthoringPrintActions"

// constants
import {
  getSubmissionSchemas,
  registryContinentMap,
  FORM_VIEW_TRANSFORMER,
  FORM_SECTION_TRANSFORMER,
  submissionColDefs,
} from "constants/submissions"
import { dateColDef } from "components/grids/columnTemplates"
import { REGISTRY_PREVIEW_TYPE } from "constants/authoring"

// utils
import fileDownload from "utilities/fileDownload"

import "./Submissions.scss"

const approvalHistoryColDefs = [
  {
    headerName: "Date",
    field: "created_at",
    ...dateColDef,
    suppressSizeToFit: true,
  },
  {
    headerName: "Time",
    field: "created_at",
    cellRenderer: TimeCellRenderer,
    suppressSizeToFit: true,
    filterParams: {
      valueFormatter: ({ value }) => dayjs(value).format("h:mm A"),
    },
  },
  {
    headerName: "User",
    field: "created_by_fullname",
  },
  {
    headerName: "Form(s)",
    field: "form_types",
    cellRenderer: FormsCellRenderer,
    filterParams: {
      keyCreator: ({ value }) => {
        if (!value.form_view || !registryContinentMap[value.form_view]) return "(Blanks)"
        let dataToReturn = []
        const str1 = FORM_VIEW_TRANSFORMER?.[value.form_view] || value.form_view
        const str2 = value.form_section ? FORM_SECTION_TRANSFORMER?.[value.form_section] || value.form_section : ""
        dataToReturn.push(str1)
        if (str2) dataToReturn.push(str2)
        return dataToReturn.join(" ")
      },
      valueFormatter: ({ value }) => {
        if (!value?.form_view || !value?.form_section || !registryContinentMap[value.form_view]) return "(Blanks)"
        let str =
          value?.form_view && value?.form_section
            ? `${FORM_VIEW_TRANSFORMER?.[value?.form_view] || value.form_view} ${
                FORM_SECTION_TRANSFORMER?.[value?.form_section] || value.form_section
              }`
            : null

        return str
      },
      comparator: (a, b) => {
        const valA = a?.form_view
        const valB = b?.form_view
        if (valA === valB) return 0
        // for (Blanks)
        if (a === null) {
          return -1
        } else if (b === null) {
          return 1
        }
        return valA > valB ? 1 : -1
      },
    },
    cellClass: ["col-badge"],
  },
  {
    headerName: "File(s)",
    field: "docs",
    cellRenderer: ({ value }) => {
      return value.map((doc) => doc.document_name).join(", ")
    },
    valueGetter: ({ data }) => {
      const docs = data?.docs || []
      const retValues = docs.map((doc, i) => {
        let parsedForm = doc
        if (typeof doc === "string") parsedForm = JSON.parse(doc)
        return parsedForm
      })
      return retValues
    },
    filterParams: {
      keyCreator: ({ value }) => value?.document_name,
      valueFormatter: ({ value }) => (value?.document_name ? value.document_name : "(Blanks)"),
    },
    comparator: (valueA, valueB) => {
      let aStr = ""
      if (Array.isArray(valueA)) {
        aStr = valueA.map((value) => value.document_name).join(", ")
      }
      let bStr = ""
      if (Array.isArray(valueB)) {
        bStr = valueB.map((value) => value.document_name).join(", ")
      }
      if (aStr == bStr) return 0
      return aStr > bStr ? 1 : -1
    },
  },
  {
    headerName: "Version",
    field: "form_version",
    cellRenderer: ({ value }) => {
      if (value === null) return "N/A"
      if (String(value)?.toLowerCase() === "N/A") return value
      else if (value < 0) return "N/A"
      return Number(value).toFixed(1).toString()
    },
  },
  {
    headerName: "Outcome",
    field: "outcome",
    valueGetter: ({ data }) => {
      if (data.is_approved) return "Approved"
      else if (data.is_rejected) return "Rejected"
      else if (data.is_onhold) return "On Hold"
      else return null
    },
  },
  {
    headerName: "eSign Failed?",
    field: "esign_failed",
    cellRenderer: "SuccessFailedCellRenderer",
    width: 120,
  },
  {
    headerName: "Comments",
    field: "comments",
  },
]

const Submissions = () => {
  const { data: userSettings } = useUserSettings()
  const { formVersion, isAutoRelease } = useSubmissions()

  const { onPrintPage } = useAuthoringPrintActions()

  const { formView, setShowUniqueRegistryPreviewType } = useAuthoringViewStore(
    useShallow((state) => ({
      formView: state.formView,
      setShowUniqueRegistryPreviewType: state.setShowUniqueRegistryPreviewType,
    }))
  )

  const { openToastNotification } = useAppState()

  const [searchSubmissions, setSearchSubmissions] = useState("")
  const [openUploadModal, setOpenUploadModal] = useState(false)
  const [openProgressModal, setOpenProgressModal] = useState(false)
  const [uploadLoading, setUploadLoading] = useState(false)

  const { studyId } = useParams()
  const { data: study } = useGetStudy(studyId)
  const { data: authoring } = useGetAuthoringStudy(study?.authoring_study_id)
  const { data: submissions = [], refetch: refetchSubmissionHistory } = useGetSubmissionsHistory(
    study?.authoring_study_id
  )
  const { data: approvals = [] } = useGetApprovalHistory(study?.authoring_study_id)

  const postSubmission = usePostSubmission(study?.authoring_study_id)
  const downloadXml = useDownloadXml(study?.authoring_study_id)
  const [queueId, setQueueId] = useState(null)
  const postPrsSubmissionsUpload = usePostPrsSubmissionsUpload()

  const { data: pollingData } = useGetPrsSubmissionsUpload(queueId, {
    refetchInterval: queueId ? 1000 : null,
    enabled: queueId ? true : false,
  })

  const [finalPollData, setFinalPollData] = useState(null)

  const onDownloadPdf = async (formTypes) => {
    const req = {
      form_types: formTypes,
      form_version: Number(formVersion.id),
      xml_type: formView.id,
    }

    const formType = formTypes?.[0]
    let showUniqueRegistryPreviewType = null
    if (formType.form_section === "Results" && formType.form_view === "CTIS") {
      showUniqueRegistryPreviewType = REGISTRY_PREVIEW_TYPE.ctisResults
      setShowUniqueRegistryPreviewType(showUniqueRegistryPreviewType)
    }

    onPrintPage({
      formVersion: Number(formVersion.id),
      isSubmissionsPage: true,
      showUniqueRegistryPreviewType,
    })
    await postSubmission(
      { body: req },
      {
        onSettled: () => {
          setOpenUploadModal(false)
          setShowUniqueRegistryPreviewType(null)
        },
      }
    )
  }

  const onDownloadXml = (formTypes) => {
    setUploadLoading(true)
    const req = {
      form_types: formTypes,
      form_version: Number(formVersion.id),
      xml_type: formView.id,
    }

    if (req.form_section === "delayedResultsCertification")
      req.form_section = getSubmissionSchemas()["delayedResultsCertification"].submitToCTG

    downloadXml(
      { body: req },
      {
        onSuccess: (res) => {
          fileDownload(
            res.data.xml_str,
            `${authoring.study_id}_${dayjs().format(userSettings?.date_format)}.xml`,
            "application/xml"
          )
          setOpenUploadModal(false)
        },
        onSettled: () => {
          setUploadLoading(false)
        },
      }
    )
  }

  // TODO: this should take an onSettled callback as a param
  const onSubmitToCtg = (payload) => {
    openToastNotification("info", "Pending", "Submitting record in progress")
    postPrsSubmissionsUpload(
      {
        body: payload,
      },
      {
        onSuccess: (resp) => {
          const { queue_id } = resp.data
          setQueueId(queue_id)
        },
        onSettled: () => {
          // callback here
        },
      }
    )
  }

  useEffect(() => {
    if (pollingData?.task_status?.complete === true && queueId) {
      setOpenProgressModal(true)
      onStopPolling(true)
      refetchSubmissionHistory()
      setFinalPollData(pollingData)
      // removePollingData()
    }
  }, [pollingData?.task_status?.complete])

  const hasErrors = useMemo(() => {
    if (
      (finalPollData?.upload_report?.failureMessage && finalPollData?.upload_report?.validationMessages) ||
      finalPollData?.task_status?.state === "FAILURE" ||
      finalPollData?.upload_report === null
    ) {
      return true
    }
    return false
  }, [finalPollData])

  const onStopPolling = () => {
    setQueueId(null)
  }

  const formattedPollData = useMemo(() => {
    if (finalPollData) {
      let in_progress = finalPollData.in_progress

      let formattedData = {
        autorelease: finalPollData.autorelease,
        in_progress,
        is_cancelled: null,
      }
      let upload_report = finalPollData?.upload_report || {}

      if (
        finalPollData?.task_status?.state === "FAILURE" ||
        (finalPollData.upload_report === null && finalPollData.upload_failed)
      ) {
        upload_report = {
          ...upload_report,
          uploadStatus: "ERROR",
          failureMessage: finalPollData?.task_status.result || "N/A",
        }
      }

      let uploadRecord = {
        study_id: finalPollData.study_id,
        receipt_url: finalPollData.receipt_url,
        upload_report,
      }
      if (finalPollData.upload_report?.documentUploadStatus) {
        uploadRecord.document_upload_status = finalPollData.upload_report.documentUploadStatus
        uploadRecord.uploaded_docs = Object.keys(finalPollData.upload_report.documentUploadStatus)
      }

      formattedData.upload_records = [uploadRecord]
      return formattedData
    }

    return null
  }, [finalPollData])

  let progressModalWidth = 600

  if (formattedPollData) {
    let firstRecord = formattedPollData.upload_records?.[0]
    if (
      hasErrors ||
      (firstRecord?.upload_report?.recordStatus &&
        firstRecord?.upload_report?.uploadStatus &&
        firstRecord.document_upload_status)
    ) {
      progressModalWidth = 1000
    }
  }

  return (
    <Flex
      vertical={true}
      gap={24}
      className="study-submissions-tab"
    >
      <Panel
        title="SUBMISSION/DOWNLOAD HISTORY"
        iconClassName="fa-light fa-cloud-arrow-down"
        actions={[
          <PrimeButton
            type="add"
            disabled={queueId ? true : false}
            loading={queueId ? true : false}
            onClick={() => setOpenUploadModal(true)}
          >
            {queueId ? "Submission in Progress" : "Create Submission"}
          </PrimeButton>,
        ]}
      >
        <Flex
          vertical={true}
          gap={12}
        >
          <div className="study-submissions-search">
            <PrimeField
              schema={{
                type: "text",
                placeholder: "Search",
              }}
              value={searchSubmissions}
              onChange={setSearchSubmissions}
            />
          </div>
          <GridTypeA
            columnDefs={submissionColDefs.filter((item) => {
              if (item.field === "study_id" || item.field === "study_title") return false
              return true
            })}
            domLayout="autoHeight"
            rowData={submissions}
            search={searchSubmissions}
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={[10, 20, 50, 100]}
          />
        </Flex>
      </Panel>
      <Panel
        title="APPROVAL HISTORY"
        iconClassName="fa-light fa-file-contract"
      >
        <GridTypeA
          columnDefs={approvalHistoryColDefs}
          rowData={approvals}
          domLayout="autoHeight"
          pagination={true}
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 20, 50, 100]}
        />
      </Panel>

      {/* upload modal */}
      <UploadModal
        authoringId={study?.authoring_study_id}
        openModal={openUploadModal}
        setOpenModal={setOpenUploadModal}
        onSubmitToCtg={onSubmitToCtg}
        onDownloadXml={onDownloadXml}
        onDownloadPdf={onDownloadPdf}
        uploadLoading={uploadLoading}
        approvalHistoryColDefs={approvalHistoryColDefs}
        approvals={approvals}
      />

      {/* upload summary modal */}
      <Modal
        title={"Upload Summary"}
        message={"The upload is complete. Please see status of uploads below."}
        open={openProgressModal}
        hideImage={true}
        footer={[]}
        width={progressModalWidth}
        onCancel={() => setOpenProgressModal(false)}
      >
        <PRSUploadProgress
          isAutoRelease={isAutoRelease}
          uploadComplete={true} // uploadComplete
          hasErrors={hasErrors}
          data={formattedPollData}
          setOpenModal={() => setOpenProgressModal(false)}
        />
      </Modal>
    </Flex>
  )
}

export default Submissions
